<template>
  <auth-card title="CSR Sign In"
    :error-message="errorMessage">

    <!-- 1. Credentials -->
    <form v-if="panel === 'login'"
      @submit.prevent="validateCredential">
      <div class="control mb-4">
        <div class="field">
          <label class="label"
            for="csr-username">Username</label>
          <input id="csr-username"
            class="input"
            type="text"
            placeholder="Username"
            autocomplete="username"
            required
            :disabled="isSubmitting"
            v-model="username">
          <span v-if="$v.username.$dirty && !$v.username.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <!-- login.css .password-container - a deliberate 40px gap before the Remember Me row. -->
        <div class="field password-field">
          <label class="label"
            for="csr-password">Password</label>
          <div class="control has-icons-right">
            <input id="csr-password"
              class="input"
              :type="passwordVisible ? 'text' : 'password'"
              placeholder="Password"
              autocomplete="current-password"
              required
              :disabled="isSubmitting"
              v-model="password">
            <span class="icon is-small is-right is-clickable"
              @click="passwordVisible = !passwordVisible">
              <i class="mdi mdi-18px"
                :class="passwordVisible ? 'mdi-eye-outline' : 'mdi-eye-off-outline'" />
            </span>
          </div>
          <span v-if="$v.password.$dirty && !$v.password.required"
            class="help is-danger ml-1">Required</span>
        </div>

        <div class="field remember-me-container
          is-flex is-justify-content-space-between is-align-items-center">
          <label class="checkbox">
            <input type="checkbox"
              :disabled="isSubmitting"
              v-model="rememberMe">
            Remember Me
          </label>
          <a @click.prevent="goToForgotPassword">Forgot Password?</a>
        </div>
      </div>

      <div class="control is-flex is-justify-content-end">
        <button type="submit"
          class="button is-primary"
          :disabled="isSubmitting">
          <span class="icon mr-1">
            <i class="mdi mdi-18px"
              :class="isSubmitting ? 'mdi-loading mdi-spin' : 'mdi-arrow-right'" />
          </span>
          Next
        </button>
      </div>
    </form>

    <!-- 2. First-time password change -->
    <new-password-panel v-else-if="panel === 'newaccount'"
      id-prefix="csr"
      :username="username"
      :is-submitting="isSubmitting"
      @submit="changePassword" />

    <!-- 3. Company selection -->
    <form v-else-if="panel === 'selectcompanies'"
      @submit.prevent="proceedWithCompany">
      <div class="control mb-4">
        <div class="field">
          <label class="label"
            for="csr-company">Company</label>
          <!-- Typeahead rather than a plain select: a support user's list runs to hundreds of
               companies, and the legacy page had a searchable combobox. -->
          <bulma-autocomplete id="csr-company"
            placeholder="Start typing a company name"
            v-model="companyQuery"
            :data="filteredCompanies"
            field="name"
            :disabled="isSubmitting"
            open-on-focus
            focus-inserted
            @typing="onCompanyTyping"
            @select="onCompanySelected">
            <template slot-scope="props">
              {{ props.option.name }}
            </template>
            <template slot="empty">
              <span class="has-text-grey">No matching company</span>
            </template>
          </bulma-autocomplete>
          <span v-if="$v.selectedCompanyId.$dirty && !$v.selectedCompanyId.required"
            class="help is-danger ml-1">Select a company from the list</span>
          <span v-else-if="!companies.length"
            class="help ml-1">No companies are available for this account.</span>
        </div>
      </div>

      <div class="control is-flex is-justify-content-space-between">
        <button type="button"
          class="button"
          :disabled="isSubmitting"
          @click="backToLogin">
          <span class="icon mr-1">
            <i class="mdi mdi-18px mdi-arrow-left" />
          </span>
          Back
        </button>
        <button type="submit"
          class="button is-primary"
          :disabled="isSubmitting || !companies.length">
          <span class="icon mr-1">
            <i class="mdi mdi-18px"
              :class="isSubmitting ? 'mdi-loading mdi-spin' : 'mdi-arrow-right'" />
          </span>
          Sign In
        </button>
      </div>
    </form>
  </auth-card>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import AuthCard from './AuthCard.vue'
import NewPasswordPanel from './NewPasswordPanel.vue'
import AuthRoutes from './route-types.js'
import { BulmaAutocomplete } from '@/components/BulmaAutocomplete'
import { getApiErrorMessage, isSignInTicketExpired } from './ErrorMessage'
import { resolveAppUrl } from './returnUrl'
import { detectPlatform } from '@/services/platform.js'

const REMEMBER_KEY = 'fq.rememberedCsrLogin'

export default {
  name: 'SignInCsrView',
  components: {
    AuthCard,
    BulmaAutocomplete,
    NewPasswordPanel
  },
  data() {
    return {
      panel: 'login',
      // login panel
      username: '',
      password: '',
      passwordVisible: false,
      rememberMe: false,
      // Short-lived single-use ticket from validate-credential. Identifies the support user for
      // every later step; the server never accepts a user id from the client.
      ticket: '',
      // companies panel
      companies: [],
      // What the user has typed. selectedCompanyId is only set by picking from the list, so a
      // half-typed name can never be submitted as a selection.
      companyQuery: '',
      selectedCompanyId: '',
      // shared
      isSubmitting: false,
      errorMessage: ''
    }
  },
  computed: {
    filteredCompanies() {
      // Filtered locally - the whole list arrives in one call, so there is nothing to fetch
      // per keystroke. An empty query shows everything, which is what open-on-focus wants.
      const query = (this.companyQuery || '').trim().toLowerCase()
      if (!query) return this.companies
      return this.companies.filter(c => (c.name || '').toLowerCase().includes(query))
    }
  },
  created() {
    try {
      const remembered = JSON.parse(localStorage.getItem(REMEMBER_KEY) || 'null')
      if (remembered && remembered.username) {
        this.username = remembered.username
        this.rememberMe = true
      }
    } catch (e) { /* corrupt entry - just start with an empty form */ }
  },
  methods: {
    async validateCredential() {
      this.errorMessage = ''
      this.$v.username.$touch()
      this.$v.password.$touch()
      if (this.$v.username.$invalid || this.$v.password.$invalid) return

      this.isSubmitting = true
      try {
        const result = await this.$auth.csr.validateCredential({
          username: this.username,
          password: this.password,
          location: ''
        })
        this.ticket = result.ticket

        if (this.rememberMe) {
          localStorage.setItem(REMEMBER_KEY, JSON.stringify({ username: this.username }))
        } else {
          localStorage.removeItem(REMEMBER_KEY)
        }

        if (result.isNewAccount) {
          this.panel = 'newaccount'
        } else {
          await this.loadCompanies()
        }
      } catch (e) {
        this.errorMessage = getApiErrorMessage(e, 'Sign In failed. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },

    async loadCompanies() {
      this.companies = await this.$auth.csr.getCompanies(this.ticket)
      this.companyQuery = ''
      this.selectedCompanyId = ''
      this.panel = 'selectcompanies'
    },

    onCompanyTyping(value) {
      this.companyQuery = value
      // Editing after choosing invalidates the choice, so a stale id cannot be submitted
      // alongside a name the user has since changed.
      this.selectedCompanyId = ''
    },

    onCompanySelected(company) {
      this.selectedCompanyId = company ? company.companyId : ''
      this.companyQuery = company ? company.name : ''
    },

    async proceedWithCompany() {
      this.errorMessage = ''
      this.$v.selectedCompanyId.$touch()
      if (this.$v.selectedCompanyId.$invalid) return

      this.isSubmitting = true
      try {
        // Straight to sign-in, matching the legacy page: SignInSupport() called
        // SignInWithLocation() directly and never consulted session-in-use. Any existing
        // session for this support user and company is upserted over.
        await this.finalSignIn()
      } catch (e) {
        if (isSignInTicketExpired(e)) {
          // Retrying from this panel can only fail again with the same dead ticket.
          // backToLogin clears errorMessage, so it has to run first.
          this.backToLogin()
          this.errorMessage = 'Your sign-in session has expired. Please enter your password again.'
        } else {
          this.errorMessage = getApiErrorMessage(e, 'Sign In failed. Please try again.')
        }
      } finally {
        this.isSubmitting = false
      }
    },

    backToLogin() {
      this.errorMessage = ''
      this.password = ''
      this.ticket = ''
      this.companies = []
      this.companyQuery = ''
      this.selectedCompanyId = ''
      this.panel = 'login'
      this.$v.$reset()
    },

    async finalSignIn() {
      const { osId, browserId } = detectPlatform()
      await this.$auth.csr.signIn({
        ticket: this.ticket,
        companyId: this.selectedCompanyId,
        osId,
        browserId,
        // See SignIn.vue - geolocation capture is out of scope for FQ-3057.
        location: ''
      })
      // Full navigation, not router.push: App.vue runs loadResources once on router.onReady,
      // so an in-app transition would render the shell with no user or permissions loaded.
      window.location.assign(resolveAppUrl(this.$route.query.returnUrl))
    },

    async changePassword(newPassword) {
      this.errorMessage = ''
      this.isSubmitting = true
      try {
        await this.$auth.changePassword({
          ticket: this.ticket,
          oldPassword: this.password,
          newPassword
        })

        // changePassword consumed the ticket, so re-validate with the new password to get a
        // fresh one, then carry on to the company step.
        this.password = newPassword
        const result = await this.$auth.csr.validateCredential({
          username: this.username,
          password: this.password,
          location: ''
        })
        this.ticket = result.ticket
        await this.loadCompanies()
      } catch (e) {
        this.errorMessage = getApiErrorMessage(e, 'Could not change your password. Please try again.')
      } finally {
        this.isSubmitting = false
      }
    },

    goToForgotPassword() {
      this.$router.push({ name: AuthRoutes.ForgotPasswordCsrView.name })
    }
  },
  validations: {
    username: { required },
    password: { required },
    selectedCompanyId: { required }
  }
}
</script>

<style lang="scss" scoped>
.label {
  font-weight: 500;
}
.password-field {
  margin-bottom: 40px;
}
.remember-me-container {
  font-size: 0.9rem;
}
</style>
